import { groupBy, uniq } from 'lodash'
import { AccountMapRule, AccountMapRuleGrouped } from '../types/AccountMapRule'

/**
 * Minimal interface for statement row ordering
 * Only requires the id field for sorting purposes
 */
export interface StatementRowOrderInfo {
  id: number
}

/**
 * Groups account map rules by statementRowId
 * Replaces the Redux selector: contextAccountMapRulesGroupedByStatementRowIdSelector
 */
export const groupRulesByStatementRowId = (rules: AccountMapRule[]): AccountMapRuleGrouped => {
  return groupBy(rules, 'statementRowId')
}

/**
 * Transformed row for multi-company grid view
 * Each row represents one "slot" for a statementRowId
 * Multiple rows can exist for the same statementRowId when companies have multiple rules
 */
export interface AccountMapGridRow {
  /** Unique row identifier for ag-grid */
  rowId: string
  /** The statement row this belongs to */
  statementRowId: number
  /** Index within the statementRowId group (0, 1, 2...) */
  rowIndex: number
  /** Company data for this row */
  companies: {
    [companyId: string]: {
      from: number | null
      to: number | null
    }
  }
}

/**
 * Company info for column generation
 */
export interface CompanyColumnInfo {
  companyId: string
  companyName: string
}

/**
 * Transforms AccountMapRule[] into grid rows grouped by statementRowId
 * Creates multiple rows per statementRowId when companies have multiple rules
 *
 * Example:
 * Input: [
 *   { statementRowId: 100, companyId: 'A', from: 1000, to: 1999 },
 *   { statementRowId: 100, companyId: 'A', from: 2000, to: 2999 },
 *   { statementRowId: 100, companyId: 'B', from: 1000, to: 1500 },
 * ]
 * Output: [
 *   { statementRowId: 100, rowIndex: 0, companies: { A: {from: 1000, to: 1999}, B: {from: 1000, to: 1500} } },
 *   { statementRowId: 100, rowIndex: 1, companies: { A: {from: 2000, to: 2999}, B: {from: null, to: null} } },
 * ]
 *
 * @param rules - Array of account map rules (can be from multiple companies)
 * @returns Array of transformed rows for the grid
 */
export const transformRulesToGridRows = (rules: AccountMapRule[]): AccountMapGridRow[] => {
  // Deduplicate rules
  const seen = new Set<string>()
  const validRules = rules.filter(r => {
    const key = `${r.statementRowId}-${r.companyId}-${r.from}-${r.to}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })

  // Group by statementRowId
  const groupedByStatement = groupBy(validRules, 'statementRowId')
  const result: AccountMapGridRow[] = []

  Object.entries(groupedByStatement).forEach(([statementRowId, statementRules]) => {
    // Further group by companyId within this statementRowId
    const groupedByCompany = groupBy(statementRules, 'companyId')

    // Find the max number of rules any company has for this statementRowId
    const maxRuleCount = Math.max(...Object.values(groupedByCompany).map(arr => arr?.length ?? 0), 0)

    // Create rows for each index
    for (let i = 0; i < maxRuleCount; i += 1) {
      const companies: AccountMapGridRow['companies'] = {}

      Object.entries(groupedByCompany).forEach(([companyId, companyRules]) => {
        const rule = companyRules[i]
        companies[companyId] = rule ? { from: rule.from, to: rule.to } : { from: null, to: null }
      })

      result.push({
        rowId: `${statementRowId}-${i}`,
        statementRowId: Number(statementRowId),
        rowIndex: i,
        companies
      })
    }
  })

  // Sort by statementRowId, then by rowIndex
  return result.sort((a, b) => a.statementRowId - b.statementRowId || a.rowIndex - b.rowIndex)
}

/**
 * Transforms grid rows back to AccountMapRule[] for saving
 *
 * @param rows - Grid rows to transform back
 * @returns Array of AccountMapRule ready for API
 */
export const transformGridRowsToRules = (rows: AccountMapGridRow[]): AccountMapRule[] => {
  const rules: AccountMapRule[] = []

  rows.forEach(row => {
    Object.entries(row.companies).forEach(([companyId, data]) => {
      // Only include if both from and to have values
      if (data && data.from != null && data.to != null) {
        rules.push({
          statementRowId: row.statementRowId,
          companyId,
          from: data.from,
          to: data.to
        })
      }
    })
  })

  return rules
}

/**
 * Extracts unique company IDs from rules
 */
export const getUniqueCompanyIds = (rules: AccountMapRule[]): string[] => {
  return uniq(rules.map(r => r.companyId))
}

/**
 * Gets all unique statementRowIds from rules
 */
export const getUniqueStatementRowIds = (rules: AccountMapRule[]): number[] => {
  return uniq(rules.map(r => r.statementRowId)).sort((a, b) => a - b)
}

/**
 * Creates an empty grid row for a given statementRowId
 *
 * @param statementRowId - The statement row ID
 * @param rowIndex - The row index within the group
 * @param companyIds - Array of company IDs to initialize
 * @returns A new empty AccountMapGridRow
 */
export const createEmptyGridRow = (
  statementRowId: number,
  rowIndex: number,
  companyIds: string[]
): AccountMapGridRow => {
  const row: AccountMapGridRow = {
    rowId: `${statementRowId}-${rowIndex}`,
    statementRowId,
    rowIndex,
    companies: {}
  }

  companyIds.forEach(companyId => {
    row.companies[companyId] = { from: null, to: null }
  })

  return row
}

/**
 * Gets the next available rowIndex for a statementRowId
 *
 * @param rows - Current grid rows
 * @param statementRowId - The statement row ID to find next index for
 * @returns The next available rowIndex (0 if no existing rows)
 */
export const getNextRowIndexForStatementRow = (rows: AccountMapGridRow[], statementRowId: number): number => {
  const existingRows = rows.filter(r => r.statementRowId === statementRowId)
  if (existingRows.length === 0) return 0
  return Math.max(...existingRows.map(r => r.rowIndex)) + 1
}

/**
 * Sorts grid rows by the order defined in statementRows array
 *
 * @param rows - Grid rows to sort
 * @param statementRows - Ordered array of statement rows (determines sort order)
 * @returns New sorted array of grid rows
 */
export const sortRowsByStatementRowOrder = (
  rows: AccountMapGridRow[],
  statementRows: StatementRowOrderInfo[]
): AccountMapGridRow[] => {
  const orderMap = new Map(statementRows.map((row, index) => [row.id, index]))

  return [...rows].sort((a, b) => {
    const orderA = orderMap.get(a.statementRowId) ?? Number.MAX_VALUE
    const orderB = orderMap.get(b.statementRowId) ?? Number.MAX_VALUE
    if (orderA !== orderB) return orderA - orderB
    return a.rowIndex - b.rowIndex
  })
}

/**
 * Ensures every statementRow has at least one grid row (for displaying add button)
 * Creates placeholder rows for statementRows that have no existing rules
 *
 * @param rows - Current grid rows
 * @param statementRows - All statement rows that should be represented
 * @param companyIds - Company IDs to initialize in placeholder rows
 * @returns Grid rows with placeholders added for missing statementRows
 */
export const ensureAllStatementRowsHaveRows = (
  rows: AccountMapGridRow[],
  statementRows: StatementRowOrderInfo[],
  companyIds: string[]
): AccountMapGridRow[] => {
  const existingStatementRowIds = new Set(rows.map(r => r.statementRowId))
  const result = [...rows]

  statementRows.forEach(sr => {
    if (!existingStatementRowIds.has(sr.id)) {
      result.push(createEmptyGridRow(sr.id, 0, companyIds))
    }
  })

  return result
}

/**
 * Merges rules from the current grid with existing rules from other tabs/sections
 * Keeps rules from statementRows NOT in the visible set, replaces rules from visible statementRows
 *
 * @param gridRules - Rules from the current grid (will replace existing)
 * @param allExistingRules - All existing rules (from server)
 * @param visibleStatementRowIds - Set of statementRowIds visible in current grid
 * @returns Merged array of rules ready for saving
 */
export const mergeRulesForSave = (
  gridRules: AccountMapRule[],
  allExistingRules: AccountMapRule[],
  visibleStatementRowIds: Set<number>
): AccountMapRule[] => {
  const rulesFromOtherTabs = allExistingRules.filter(rule => !visibleStatementRowIds.has(rule.statementRowId))
  return [...rulesFromOtherTabs, ...gridRules]
}

/**
 * Transforms server data (AccountMapRule[]) to grid rows with filtering and sorting
 * This is a pure function that can be used both in useMemo and for resetting data
 *
 * @param serverData - Rules from the server (can be undefined)
 * @param statementRows - Ordered list of statementRows for filtering and sorting
 * @param companyIds - Company IDs to initialize in placeholder rows
 * @param includePlaceholders - When true, injects empty placeholder rows for statement rows
 *   that have no rules (so the UI can show an add button for every row). Set to false to
 *   get only rows that have actual data, avoiding ghost rows for cleared statement rows.
 * @returns Transformed, filtered, and sorted grid rows
 */
export const transformServerDataToGridRows = (
  serverData: AccountMapRule[] | undefined,
  statementRows: StatementRowOrderInfo[] | undefined,
  companyIds: string[],
  includePlaceholders = true
): AccountMapGridRow[] => {
  // Filter to only include rules for visible companies
  const companyIdSet = new Set(companyIds)
  const filteredData = serverData?.filter(r => companyIdSet.has(r.companyId))
  let rows = filteredData ? transformRulesToGridRows(filteredData) : []

  if (statementRows && statementRows.length > 0) {
    // Filter to only include rows for current form's statementRows
    const validStatementRowIds = new Set(statementRows.map(sr => sr.id))
    rows = rows.filter(row => validStatementRowIds.has(row.statementRowId))

    if (includePlaceholders) {
      // Ensure every statementRow has at least one row (for visual hierarchy)
      rows = ensureAllStatementRowsHaveRows(rows, statementRows, companyIds)
    }
    // Sort by statementRows order
    rows = sortRowsByStatementRowOrder(rows, statementRows)
  }

  return rows
}
